/**
 * Version Banner Component
 *
 * Displays deployment version information with:
 * - Visible build ID and git hash
 * - Click to expand full details
 * - Fetches and compares worker version
 * - Highlights version mismatches
 */

import { useState, useEffect, useCallback } from 'react';
import { Popover, Typography, Space, Tag, Spin, Button, Tooltip, message } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  SyncOutlined,
  CopyOutlined,
  CloudServerOutlined,
  DesktopOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { BUILD_VERSION } from '../version';

const { Text } = Typography;

interface WorkerVersion {
  id: string;
  timestamp: string;
  git: string;
  branch: string;
  env?: string;
}

interface HealthResponse {
  status: string;
  timestamp: string;
  build: WorkerVersion;
}

export default function VersionBanner() {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [workerVersion, setWorkerVersion] = useState<WorkerVersion | null>(null);
  const [workerError, setWorkerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch worker version
  const fetchWorkerVersion = useCallback(async () => {
    setLoading(true);
    setWorkerError(null);

    try {
      // Use the API URL from environment or relative path
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const healthUrl = apiUrl.replace(/\/api$/, '') + '/health';

      const response = await fetch(healthUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: HealthResponse = await response.json();
      setWorkerVersion(data.build);
    } catch (error) {
      setWorkerError(error instanceof Error ? error.message : 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on popover open
  useEffect(() => {
    if (popoverOpen && !workerVersion && !loading) {
      fetchWorkerVersion();
    }
  }, [popoverOpen, workerVersion, loading, fetchWorkerVersion]);

  // Check version match
  const versionsMatch = workerVersion?.id === BUILD_VERSION.id;
  const gitMatch = workerVersion?.git === BUILD_VERSION.git;

  // Copy version info to clipboard
  const copyVersionInfo = useCallback(() => {
    const info = `Frontend:
  Build ID: ${BUILD_VERSION.id}
  Git: ${BUILD_VERSION.git}
  Branch: ${BUILD_VERSION.branch}
  Timestamp: ${BUILD_VERSION.timestamp}

Worker:
  Build ID: ${workerVersion?.id || 'Unknown'}
  Git: ${workerVersion?.git || 'Unknown'}
  Branch: ${workerVersion?.branch || 'Unknown'}
  Timestamp: ${workerVersion?.timestamp || 'Unknown'}

Versions Match: ${versionsMatch ? 'Yes' : 'No'}`;

    navigator.clipboard.writeText(info);
    message.success('Version info copied to clipboard');
  }, [workerVersion, versionsMatch]);

  // Status indicator
  const getStatusIndicator = () => {
    if (loading) {
      return <SyncOutlined spin style={{ color: 'var(--status-info)' }} />;
    }
    if (workerError) {
      return <WarningOutlined style={{ color: 'var(--status-warning)' }} />;
    }
    if (versionsMatch && gitMatch) {
      return <CheckCircleOutlined style={{ color: 'var(--status-success)' }} />;
    }
    return <WarningOutlined style={{ color: 'var(--status-error)' }} />;
  };

  // Popover content
  const popoverContent = (
    <div style={{ minWidth: 320, maxWidth: 400 }}>
      <div style={{ marginBottom: 16 }}>
        <Space align="center">
          <Text strong style={{ fontSize: 14 }}>Deployment Version</Text>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={copyVersionInfo}
          />
          <Button
            type="text"
            size="small"
            icon={<SyncOutlined spin={loading} />}
            onClick={fetchWorkerVersion}
            disabled={loading}
          />
        </Space>
      </div>

      {/* Frontend Version */}
      <div style={{ marginBottom: 12 }}>
        <Space align="center" style={{ marginBottom: 4 }}>
          <DesktopOutlined style={{ color: 'var(--status-info)' }} />
          <Text strong>Frontend</Text>
        </Space>
        <div style={{ paddingLeft: 22 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px 8px', fontSize: 12 }}>
            <Text type="secondary">Build ID:</Text>
            <Text code style={{ fontSize: 11 }}>{BUILD_VERSION.id}</Text>
            <Text type="secondary">Git:</Text>
            <Text code style={{ fontSize: 11 }}>{BUILD_VERSION.git}</Text>
            <Text type="secondary">Branch:</Text>
            <Text style={{ fontSize: 12 }}>{BUILD_VERSION.branch}</Text>
            <Text type="secondary">Built:</Text>
            <Text style={{ fontSize: 12 }}>{BUILD_VERSION.timestamp}</Text>
          </div>
        </div>
      </div>

      {/* Worker Version */}
      <div style={{ marginBottom: 12 }}>
        <Space align="center" style={{ marginBottom: 4 }}>
          <CloudServerOutlined style={{ color: '#722ed1' }} />
          <Text strong>Worker (API)</Text>
        </Space>
        <div style={{ paddingLeft: 22 }}>
          {loading ? (
            <Spin size="small" />
          ) : workerError ? (
            <Text type="danger" style={{ fontSize: 12 }}>
              Error: {workerError}
            </Text>
          ) : workerVersion ? (
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px 8px', fontSize: 12 }}>
              <Text type="secondary">Build ID:</Text>
              <Space>
                <Text code style={{ fontSize: 11 }}>{workerVersion.id}</Text>
                {!versionsMatch && <Tag color="error" style={{ fontSize: 10, margin: 0 }}>MISMATCH</Tag>}
              </Space>
              <Text type="secondary">Git:</Text>
              <Space>
                <Text code style={{ fontSize: 11 }}>{workerVersion.git}</Text>
                {!gitMatch && <Tag color="warning" style={{ fontSize: 10, margin: 0 }}>DIFFERS</Tag>}
              </Space>
              <Text type="secondary">Branch:</Text>
              <Text style={{ fontSize: 12 }}>{workerVersion.branch}</Text>
              <Text type="secondary">Built:</Text>
              <Text style={{ fontSize: 12 }}>{workerVersion.timestamp}</Text>
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>Click refresh to check</Text>
          )}
        </div>
      </div>

      {/* Status Summary */}
      <div
        style={{
          marginTop: 12,
          padding: '8px 12px',
          borderRadius: 6,
          background: versionsMatch && gitMatch
            ? 'var(--status-success-subtle)'
            : workerError
              ? 'var(--status-warning-subtle)'
              : 'var(--status-error-subtle)',
          border: `1px solid ${
            versionsMatch && gitMatch ? '#b7eb8f' : workerError ? '#ffe58f' : '#ffccc7'
          }`,
        }}
      >
        <Space>
          {getStatusIndicator()}
          <Text style={{ fontSize: 12 }}>
            {loading && 'Checking versions...'}
            {!loading && workerError && 'Could not verify worker version'}
            {!loading && !workerError && versionsMatch && gitMatch && 'All versions match!'}
            {!loading && !workerError && (!versionsMatch || !gitMatch) && (
              <>
                <strong>Version mismatch detected!</strong>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Frontend and worker may be out of sync. Try refreshing the page or redeploying.
                </Text>
              </>
            )}
          </Text>
        </Space>
      </div>

      {/* Environment */}
      {(BUILD_VERSION as any).env && (
        <div style={{ marginTop: 8 }}>
          <Tag color={(BUILD_VERSION as any).env === 'production' ? 'green' : 'blue'}>
            {(BUILD_VERSION as any).env}
          </Tag>
        </div>
      )}
    </div>
  );

  // Determine banner style based on state
  const getBannerStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: 'fixed',
      bottom: 8,
      right: 12,
      padding: '6px 12px',
      fontSize: 11,
      fontFamily: 'monospace',
      borderRadius: 6,
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      zIndex: 1000,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      transition: 'all 0.2s',
    };

    if (workerVersion && !versionsMatch) {
      return {
        ...baseStyle,
        background: 'var(--status-error-subtle)',
        border: '1px solid #ffccc7',
        color: 'var(--status-error)',
      };
    }

    return {
      ...baseStyle,
      background: 'rgba(255,255,255,0.95)',
      border: '1px solid #d9d9d9',
      color: '#666',
    };
  };

  return (
    <Popover
      content={popoverContent}
      title={null}
      trigger="click"
      open={popoverOpen}
      onOpenChange={setPopoverOpen}
      placement="topRight"
    >
      <div style={getBannerStyle()}>
        <Tooltip title="Click for deployment details">
          <Space size={6}>
            <InfoCircleOutlined />
            <span>v{BUILD_VERSION.id.slice(-6)}</span>
            <span style={{ opacity: 0.7 }}>|</span>
            <span>{BUILD_VERSION.git}</span>
            {workerVersion && !versionsMatch && (
              <WarningOutlined style={{ color: 'var(--status-error)' }} />
            )}
          </Space>
        </Tooltip>
      </div>
    </Popover>
  );
}
